import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MANAGER_PAGES, PermissionMap, hasAny, homePathFor } from '../utils/permissions';

interface User {
  id: string; // Staff ID (MaNhanVien)
  username: string;
  name: string;
  role: string; // bộ phận, chỉ để hiển thị
  permissions: PermissionMap;
  isAdmin: boolean; // có ít nhất một trang quản lý được phép
  homePath: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  can: (...keys: string[]) => boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

function toUser(raw: any): User {
  const permissions: PermissionMap = raw?.permissions && typeof raw.permissions === 'object' ? raw.permissions : {};
  return {
    id: raw.id,
    username: raw.username,
    name: raw.name,
    role: raw.role,
    permissions,
    isAdmin: MANAGER_PAGES.some((page) => page.inNavigation && hasAny(permissions, page.permissions)),
    homePath: homePathFor(permissions),
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
  };

  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 401) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
        if (!url.includes('/api/auth/login')) {
          logout();
        }
      }
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      const savedUser = localStorage.getItem('user');
      const token = localStorage.getItem('auth_token');

      if (!savedUser || !token) {
        logout();
        setIsLoading(false);
        return;
      }

      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(toUser(parsedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
        logout();
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/auth/verify', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            const userData = toUser(data.user);
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          } else {
            logout();
          }
        } else if (response.status === 401 || response.status === 403) {
          console.warn('Phiên đăng nhập đã hết hạn hoặc tài khoản đã nghỉ việc.');
          logout();
        }
      } catch (error) {
        console.error('Error verifying auth with backend:', error);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success) {
        const userData = toUser(data.user);
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        if (data.token) localStorage.setItem('auth_token', data.token);
        return true;
      } else {
        console.error('Login failed:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isAdmin: user?.isAdmin || false,
    can: (...keys: string[]) => hasAny(user?.permissions, keys),
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
